/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import {
  type ProductFormEntry,
  type ReviewCandidate,
} from '../../../../../features/customReviews/interface/types';
import { Rating } from '../../../../../features/reviews/components/SubmitProductReviewModal/components/Rating/Rating';
import { SubmitReviewButton } from '@/features/reviews/components/SubmitReviewButton/SubmitReviewButton';
import { Skeleton, useNotification, Text } from '@az/starc-ui';
import { useEffect, useState } from 'react';
import styles from '../styles.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { usePageType } from '@/hooks/usePageType';
import { useProgressiveSubmissionMutation } from '@/features/customReviews/api/postProgressiveSubmission';
import { useLocale } from '@/hooks/useLocale';
import { formatYmmeVehicleString } from '@/features/customReviews/utils/formatYmmeVehicleString';
import { getFingerprint } from '@/features/reviews/utils/getFingerprint';
import logger from '@/utils/logger';
import { normalizeErrorForLogging } from '@/utils/errorsHandling/normalizeErrorForLogging';
import { type SubmitReviewPageType } from '../../../../../features/reviews/interface';
import { useActionCandidates } from '@/components/ActionCandidates/context/ActionCandidatesProvider';
import { useHeaderData } from '@/features/header/api/getHeader';

type ReviewProductProps = {
  product: ReviewCandidate;
  formData?: ProductFormEntry;
};

const labelMap = {
  writeAReview: 'label_reviews_writeAReview2',
  errorNotificationTitle: 'label_reviews_submit_reviewErrorNotificationTitle',
  errorNotificationText: 'label_reviews_submit_errorNotificationText',
};

export const ReviewProduct = ({ product, formData }: ReviewProductProps) => {
  const { setLatestProducts, isInitiationPending } = useActionCandidates();
  const [rating, setRating] = useState<number | null>(null);
  const productId = product.sku;
  const labels = useLabels(labelMap);
  const { data: myProfile } = useMyProfile();
  const pageType = usePageType().pageType as SubmitReviewPageType | undefined;
  const notification = useNotification();
  const { mutateAsync, isPending } = useProgressiveSubmissionMutation();
  const locale = useLocale();
  const rewardsId = useHeaderData({ select: (data) => data.myAccountMap?.rewardsId }).data;
  const vehicle = product.ymme;
  const ymmeVehicleString = formatYmmeVehicleString(vehicle) ?? undefined;

  useEffect(() => {
    if (formData?.review?.rating) {
      setRating(formData.review.rating);
    }
  }, [formData?.review?.rating]);

  const showErrorNotification = () => {
    const id = notification.show({
      type: 'error',
      title: labels.errorNotificationTitle,
      text: labels.errorNotificationText,
      position: 'top-end',
      hasCloseButton: true,
      onClose: () => notification.hide(id),
    });
  };

  const removeProduct = () => {
    setLatestProducts?.((prev) => {
      const newProducts = prev.filter((p) => p.sku !== product.sku);
      return newProducts;
    });
  };

  const openSubmitReview = async (stars: number | null) => {
    try {
      setRating(stars);

      const { firstName: usernickname, email: useremail } = myProfile ?? {};
      const submissionSessionToken = formData?.submissionSessionToken;

      if (formData?.review?.duplicateReview) {
        showErrorNotification();
        setRating(null);
        removeProduct();
        return;
      }

      if (!submissionSessionToken || !usernickname || !useremail || !pageType || stars === null) {
        showErrorNotification();
        setRating(null);
        return;
      }

      const fp = getFingerprint();

      const vehicleFields = vehicle
        ? Object.fromEntries(
            Object.entries({
              additionalfield_Vehicle: ymmeVehicleString,
              additionalfield_Vehicle_Year: vehicle.year,
              additionalfield_Vehicle_Make: vehicle.makeId,
              additionalfield_Vehicle_Model: vehicle.modelId,
              additionalfield_Vehicle_SubModel: vehicle.subModelId ?? undefined,
              additionalfield_Vehicle_Engine: vehicle.engineId ?? undefined,
            }).filter(([, v]) => v !== undefined && v !== ''),
          )
        : {};

      try {
        await mutateAsync({
          submissionFields: {
            rating: stars,
            usernickname,
            useremail,
            isratingsonly: true,
            ...(rewardsId ? { additionalField_RewardsID: rewardsId } : {}),
            ...vehicleFields,
          },
          locale,
          productId: productId,
          submissionSessionToken,
          pageType,
          verifiedPurchaser: product.uas ? 'True' : 'False',
          fp,
          userToken: product.uas,
        });
      } catch (error) {
        setRating(null);
        showErrorNotification();
        logger.error({
          message: 'Failed to submit review data',
          meta: {
            source: 'ProductCard',
            error,
          },
        });
        return;
      }
    } catch (error) {
      logger.error({
        message: 'Failed to get review form data',
        meta: {
          source: 'ProductCard',
          error: normalizeErrorForLogging(error),
        },
      });
    }
  };

  return (
    <>
      <Text variant="small-body" className={styles.productName}>
        {product.productName}
      </Text>
      <FlexGrid className={styles.starsAndButton}>
        {isInitiationPending ? (
          <Skeleton
            className={styles.starsSkeleton}
            attributes={{ 'data-testid': 'rc-skeleton' }}
          />
        ) : (
          <>
            <Rating
              name={`rating-${productId}`}
              id={`rating-${productId}`}
              labelID={`rating-${productId}`}
              value={rating}
              inputClassName={styles.ratingIconContainer}
              iconClassName={styles.ratingIcon}
              onChange={openSubmitReview}
              disabled={isPending}
            />
            <SubmitReviewButton
              productId={product.sku}
              className={styles.actionButton}
              defaultValues={{
                useremail: myProfile?.email,
                usernickname: myProfile?.firstName,
                rating: rating,
                vehicle: ymmeVehicleString || undefined,
                vehicleYear: vehicle?.year,
                vehicleMake: vehicle?.makeId,
                vehicleModel: vehicle?.modelId,
                vehicleSubModel: vehicle?.subModelId ?? undefined,
                vehicleEngine: vehicle?.engineId ?? undefined,
                vehicleFromPurchase: !!ymmeVehicleString,
                uas: product.uas,
              }}
              pageType={pageType}
              label={
                <Text variant="small-body" weight="bold" className={styles.smallBody}>
                  {labels.writeAReview}
                </Text>
              }
              variant="pill-secondary"
              productName={product.productName}
              productImageUrl={product.imageUrl}
            />
          </>
        )}
      </FlexGrid>
    </>
  );
};
